import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import FormConstants from '@shared/constants/FormConstants';
import {getSearchResultsData} from '@shared/services/service-handlers/PrivateService';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {useSnackbar} from 'notistack';
import React, {ReactElement, useEffect, useRef, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import EncryptUtils from 'src/utils/EncryptUtils';
import BasicDatePicker from '@components/form/date-picker/BasicDatePicker';
import {Button, Card, Stack, useTheme} from '@mui/material';
import {useAppContext} from '@context/AppContext';
import {RHFAutocomplete} from 'src/minimal-components/hook-form';
import {PulseLoader} from 'react-spinners';
import dayjs from 'dayjs';
import {environment} from '@shared/config/environment';

interface LegacyAppDSFilterProps {
  setParentLegacyValue?: (data: string) => void;
  setSelectedFolder?: (data: object) => void;
  record_id: string;
  sectionUIData?: any;
  defaultLegacyAppValue?: string;
  selectedFolder?: {Folder?: any; date_range?: any};
}

const LegacyAppDSFilter = (
  props: LegacyAppDSFilterProps
): React.ReactElement => {
  const theme = useTheme();
  const {
    appState: {applicationName}
  } = useAppContext();

  const {
    setParentLegacyValue,
    setSelectedFolder,
    defaultLegacyAppValue,
    sectionUIData,
    selectedFolder
  } = props;
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const params = useParams();
  const [state, setState] = useState({
    dropdownValues: [],
    folderDropdown: [],
    fromDate: null,
    toDate: null,
    legacyValue: ''
  });

  const parsedminDate = dayjs(state.fromDate);
  const isValidDatey = parsedminDate.isValid();

  const parsedmaxDate = dayjs(state.toDate);
  const isValidDatex = parsedmaxDate.isValid();

  const [isLoaded, setIsLoaded] = useState(false);

  const {dropdownValues, folderDropdown} = state;

  const prevFolderIdRef = useRef<string | null>(null);

  const {fileCabinetDateFilterAutoPopulation} = environment;
  let disableFilterButton = false;

  if (
    !state?.fromDate ||
    !state?.toDate ||
    isNaN(new Date(state.fromDate).getTime()) ||
    isNaN(new Date(state.toDate).getTime())
  ) {
    disableFilterButton = true;
  }

  const {
    control,
    setValue,
    reset,
    formState: {errors}
  } = useForm({
    mode: 'onChange'
  });

  const sqlData = {
    // eslint-disable-next-line max-len
    sql: sectionUIData?.legacy_app_dropdown_sql,
    variables: [{}],
    schema: sectionUIData?.filter_schema_clause,
    filter_schema_clause: sectionUIData?.filter_schema_clause
  };
  const init = async () => {
    try {
      const data: any = {
        sql_data: sqlData,
        is_legacy_app_dropdown: sectionUIData.legacy_app_dropdown_sql,
        add_document_security_ids: true
      };
      const encryptRequestData = EncryptUtils.encryptedData(data);
      const result = await getSearchResultsData(
        applicationName,
        params.module_name || '',
        {
          tk: encryptRequestData
        }
      );
      setState((prevState: any) => ({
        ...prevState,
        legacyValue: '',
        dropdownValues: result?.data
      }));
      // setIsLoaded(true);
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  // pre-filling the form
  const setInitialValues = () => {
    setValue(FormConstants.LEGACY_APP.LEGACY_APP, defaultLegacyAppValue);
  };
  useEffect(() => {
    init();
    setInitialValues();

    if (setParentLegacyValue && typeof setParentLegacyValue === 'function') {
      setParentLegacyValue('');
    }
  }, []);

  const handleLegacyAppChange = async (legacyApp: string) => {
    sqlData.sql = sectionUIData?.folderList_dropdown_sql?.replaceAll(
      '###LegacyApp',
      `'${legacyApp}'`
    );
    try {
      setIsLoaded(true);
      const data: any = {
        sql_data: sqlData,
        is_legacy_app_dropdown: sectionUIData.legacy_app_dropdown_sql,
        add_document_security_ids: true,
        dropdown_key: 'Folder'
      };
      const encryptRequestData = EncryptUtils.encryptedData(data);
      const result = await getSearchResultsData(
        applicationName,
        params.module_name || '',
        {
          tk: encryptRequestData
        }
      );
      setState((prevState: any) => ({
        ...prevState,
        folderDropdown: result?.data
      }));
      setIsLoaded(false);
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setIsLoaded(false);
    }
  };

  useEffect(() => {
    if (defaultLegacyAppValue) {
      handleLegacyAppChange(defaultLegacyAppValue);
    }
  }, [defaultLegacyAppValue]);

  async function handleFolderChange(
    folderName: string | null,
    defaultLegacyAppValue?: string | undefined
  ) {
    const safeFolderName = folderName?.replace(/'/g, "''");
    sqlData.sql = sectionUIData?.minMax_date_sql
      ?.replaceAll('###LegacyApp', `'${defaultLegacyAppValue}'`)
      .replaceAll('###FolderName', `'${safeFolderName}'`);
    try {
      setIsLoaded(true);
      const data: any = {
        sql_data: sqlData,
        is_legacy_app_dropdown: sectionUIData.legacy_app_dropdown_sql,
        add_document_security_ids: true,
        dropdown_key: 'Folder'
      };
      const encryptRequestData = EncryptUtils.encryptedData(data);
      const result = await getSearchResultsData(
        applicationName,
        params.module_name || '',
        {
          tk: encryptRequestData
        }
      );
      if (result?.data[0]?.fromdate === null || result?.data[0]?.todate) {
        reset();
      }
      setState((prevState: any) => ({
        ...prevState,
        // folderDropdown: result?.data
        fromDate: result?.data[0]?.fromdate || null,
        toDate: result?.data[0]?.todate || null
      }));
      setIsLoaded(false);
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setIsLoaded(false);
    }
  }

  useEffect(() => {
    const matchedApp = fileCabinetDateFilterAutoPopulation.find(
      (entry: any) =>
        entry.legacyApp === defaultLegacyAppValue &&
        entry.fromAndToDateFilterAutoPopulaionWithMinMaxEnabled
    );

    const currentFolderId = selectedFolder?.Folder?.ID;

    if (matchedApp && currentFolderId) {
      const prevFolderId = prevFolderIdRef.current;

      const isSameFolder = prevFolderId === currentFolderId;

      if (!isSameFolder && defaultLegacyAppValue !== '') {
        handleFolderChange(selectedFolder.Folder.Folder, defaultLegacyAppValue);
      }

      // Update for next comparison
      prevFolderIdRef.current = currentFolderId;
    }
    if (
      defaultLegacyAppValue !== '' &&
      selectedFolder?.date_range === undefined
    ) {
      handleFolderChange(null, defaultLegacyAppValue);
    }
  }, [selectedFolder, defaultLegacyAppValue]);
  const formattedDropdownValues = dropdownValues.map((option: any) => ({
    label: option?.LegacyApp || ''
  }));

  const formattedFolderDropdownValues = folderDropdown.map((option: any) => ({
    label: option?.Folder || ''
  }));

  const renderDatesDropDown = (inputName: string): ReactElement => {
    return (
      <Stack
        display={'flex'}
        width={'100%'}
        direction={'row'}
        justifyContent={'space-between'}>
        <Stack direction="row" spacing={2}>
          <Controller
            name={inputName + 'From'}
            control={control}
            defaultValue={null}
            render={({field}) => (
              <BasicDatePicker
                label={'From'}
                value={isValidDatey ? parsedminDate : null}
                error={!!errors[inputName]}
                helperText={`${sectionUIData?.fields?.start_date?.heading?.[0]} is required`}
                field={field}
                monthYearFormatView={
                  sectionUIData?.fields?.start_date?.monthYearFormatView
                    .length > 0
                    ? true
                    : false
                }
                handleChange={e => {
                  setState(prevState => ({
                    ...prevState,
                    fromDate: e,
                    formError: ''
                  }));
                }}
              />
            )}
          />
          <Controller
            name={inputName + 'To'}
            control={control}
            defaultValue={null}
            render={({field}) => (
              <BasicDatePicker
                label={'To'}
                value={isValidDatex ? parsedmaxDate : null}
                error={!!errors[inputName]}
                helperText={`${sectionUIData?.fields?.end_date?.heading?.[0]} is required`}
                field={field}
                monthYearFormatView={
                  sectionUIData?.fields?.start_date?.monthYearFormatView
                    .length > 0
                    ? true
                    : false
                }
                handleChange={e => {
                  setState(prevState => ({
                    ...prevState,
                    toDate: e,
                    formError: ''
                  }));
                }}
              />
            )}
          />
        </Stack>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            sx={{
              width: '5rem',
              backgroundColor: theme.palette.primary.light
            }}
            onClick={() => {
              if (setSelectedFolder) {
                setSelectedFolder((prevState: any) => {
                  return {
                    ...prevState,
                    date_range: {},
                    isLoaded: false
                  };
                });
              }
              prevFolderIdRef.current = null;
              // Clear state fromDate and toDate
              const selectedValue = '';
              setState((prevState: any) => ({
                ...prevState,
                folderDropdown: [],
                legacyValue: selectedValue,
                fromDate: null,
                toDate: null
              }));
              reset();
              if (
                setParentLegacyValue &&
                typeof setParentLegacyValue === 'function'
              ) {
                setParentLegacyValue(selectedValue);
              }
            }}>
            Clear
          </Button>
          <Button
            variant="contained"
            sx={{
              width: '5rem',
              backgroundColor: theme.palette.primary.dark
            }}
            disabled={disableFilterButton}
            onClick={() => {
              if (setSelectedFolder) {
                setSelectedFolder((prevState: any) => {
                  return {
                    ...prevState,
                    date_range: {
                      fromDate: state?.fromDate,
                      toDate: state?.toDate
                    },
                    isLoaded: false
                  };
                });
              }
            }}>
            Filter
          </Button>
        </Stack>
      </Stack>
    );
  };

  const renderFoldersDropDown = (inputName: string): ReactElement => {
    return (
      <Stack display={'flex'} width={'100%'} direction={'row'} spacing={2}>
        <RHFAutocomplete
          name={inputName}
          autoHighlight
          fullWidth
          label={t('PRIVATE.LEGACY_APP.SELECT_FOLDER', {ns: 'private'})}
          placeholder={t('PRIVATE.LEGACY_APP.SELECT_FOLDER', {
            ns: 'private'
          })}
          options={formattedFolderDropdownValues}
          getOptionLabel={(option: any) => option.label}
          renderOption={(props, option) => (
            <li {...props} key={option.label}>
              {option.label}
            </li>
          )}
          onChange={(event: any, newValue: any) => {
            const selectedValue = newValue?.label ?? '';
            setState((prevState: any) => ({
              ...prevState,
              selectedFolder: selectedValue
            }));
            if (setSelectedFolder && typeof setSelectedFolder === 'function') {
              setSelectedFolder((prevState: any) => {
                return {
                  ...prevState,
                  Folder: folderDropdown.filter(
                    (item: any) => item.Folder == selectedValue
                  )?.[0],
                  isLoaded: false
                };
              });
            }
          }}
          sx={{
            width: '30%'
          }}
        />
        {/* <Controller
          name={inputName}
          control={control}
          render={({field: {value, onChange}}) => (
            <FormControlDiv width="20%">
              <SelectInput
                enableDefaultOption={false}
                options={folderDropdown || []}
                label={t('PRIVATE.LEGACY_APP.SELECT_FOLDER', {ns: 'private'})}
                placeholder={t('PRIVATE.LEGACY_APP.SELECT_FOLDER', {
                  ns: 'private'
                })}
                name={inputName}
                value={value}
                fullWidth
                onChange={(e: any) => {
                  onChange(e);
                  setState((prevState: any) => ({
                    ...prevState,
                    selectedFolder: e.target.value
                  }));
                  if (
                    setSelectedFolder &&
                    typeof setSelectedFolder === 'function'
                  ) {
                    setSelectedFolder((prevState: any) => {
                      return {
                        ...prevState,
                        Folder: folderDropdown.filter(
                          (item: any) => item.Folder == e.target.value
                        )?.[0],
                        isLoaded: false
                      };
                    });
                  }
                }}
                optionValueKey={FormConstants.LEGACY_APP.FOLDER}
                optionLabelKey={FormConstants.LEGACY_APP.FOLDER}
              />
            </FormControlDiv>
          )}
        /> */}
        {renderDatesDropDown('Date')}
      </Stack>
    );
  };

  const renderSelectFields = (inputName: string): ReactElement => {
    return (
      <Stack width={'20%'}>
        <RHFAutocomplete
          name={inputName}
          autoHighlight
          fullWidth
          label={t('PRIVATE.LEGACY_APP.SELECT_LEGACY_APP', {ns: 'private'})}
          placeholder={t('PRIVATE.LEGACY_APP.SELECT_LEGACY_APP', {
            ns: 'private'
          })}
          options={formattedDropdownValues}
          getOptionLabel={(option: any) => option.label}
          renderOption={(props, option) => (
            <li {...props} key={option.label}>
              {option.label}
            </li>
          )}
          onChange={(event: any, newValue: any) => {
            const selectedValue = newValue?.label ?? '';

            // Avoid resetting if the same value is selected
            if (selectedValue === state.legacyValue) {
              return;
            }

            if (setSelectedFolder) {
              setSelectedFolder({});
            }

            setState((prevState: any) => ({
              ...prevState,
              legacyValue: selectedValue,
              folderDropdown: [],
              selectedFolder: null,
              fromDate: null,
              toDate: null
            }));

            reset();

            if (
              setParentLegacyValue &&
              typeof setParentLegacyValue === 'function'
            ) {
              setParentLegacyValue(selectedValue);
            }
          }}
        />
      </Stack>
    );
  };

  return (
    <Card
      sx={{
        p: 3,
        display: 'flex',
        width: '100%',
        boxShadow: 5,
        borderRadius: '8px',
        gap: 2
      }}>
      {renderSelectFields(FormConstants.LEGACY_APP.LEGACY_APP)}
      {state.legacyValue !== '' && renderDatesDropDown('Date')}
      {/* {folderDropdown?.length ? renderFoldersDropDown('FolderDropDown') : null} */}
      {!isLoaded ? null : (
        <PulseLoader
          style={{
            marginTop: '17px'
          }}
          size={12}
          color={theme.palette.primary.main}
        />
      )}
      {/* {!isLoaded ? null : (
        <SyncLoader
          style={{
            marginTop: '17px'
          }}
          size={12}
          color={theme.palette.primary.main}
        />
      )} */}
      {/* {!isLoaded ? null : <CircularProgress />} */}
    </Card>
  );
};

export default LegacyAppDSFilter;

